// Bond Bastion — game data
// Same four-phase shape as the other levels: Recon (tag real bonds) ->
// Planning (build a zero-based bond portfolio) -> Campaign (defend it
// through a year of economic events) -> Debrief (real bond math + save).
//
// All coupons, ratings, and durations are simplified, illustrative figures
// for teaching purposes — not financial advice, and not a promise of
// real-world results.

export const PORTFOLIO_BUDGET = 10000;

// ---------------------------------------------------------------------------
// STAGE 1 — RECON: six real bond types. Two universal tags: how exposed each
// one is to interest-rate risk (driven by term/duration), and how exposed it
// is to credit risk (driven by rating).
// ---------------------------------------------------------------------------
export const TERM_OPTIONS = [
  { id: "SHORT", label: "Short-Term" },
  { id: "MEDIUM", label: "Medium-Term" },
  { id: "LONG", label: "Long-Term" },
];

export const CREDIT_OPTIONS = [
  { id: "LOW", label: "Low" },
  { id: "MEDIUM", label: "Medium" },
  { id: "HIGH", label: "High" },
];

export const RECON_BONDS = [
  {
    id: "tbond",
    title: "U.S. Treasury Bond",
    description: "30-year term. 4.2% coupon. Rated AAA — backed by the federal government.",
    correctTerm: "LONG",
    correctCredit: "LOW",
    note: "Virtually no default risk, but a 30-year term means its price swings hard when rates move — that's duration at work.",
  },
  {
    id: "tbill",
    title: "U.S. Treasury Bill",
    description: "6-month term. 5.1% coupon. Rated AAA.",
    correctTerm: "SHORT",
    correctCredit: "LOW",
    note: "Short term means low duration — rate changes barely move its price at all.",
  },
  {
    id: "muni",
    title: "Municipal Bond",
    description: "10-year term. 3.5% coupon, exempt from federal income tax. Rated AA.",
    correctTerm: "MEDIUM",
    correctCredit: "LOW",
    note: "That 3.5% is often worth more than it looks once you factor in the tax exemption.",
  },
  {
    id: "igcorp",
    title: "Acme Utilities Corporate Bond",
    description: "7-year term. 5.8% coupon. Rated BBB (investment-grade).",
    correctTerm: "MEDIUM",
    correctCredit: "MEDIUM",
    note: "Investment-grade, but a step down from AAA — a real, if modest, chance of trouble down the road.",
  },
  {
    id: "junk",
    title: "Rocket Startup Inc. Corporate Bond",
    description: "5-year term. 9.5% coupon. Rated B (high-yield / \u201cjunk\u201d).",
    correctTerm: "MEDIUM",
    correctCredit: "HIGH",
    note: "That fat coupon is compensation for real default risk — high-yield bonds pay more because they can pay you back less.",
  },
  {
    id: "tips",
    title: "Treasury Inflation-Protected Security (TIPS)",
    description: "10-year term. 1.8% coupon, principal adjusts upward with inflation. Rated AAA.",
    correctTerm: "MEDIUM",
    correctCredit: "LOW",
    note: "The low coupon is the price of built-in inflation protection — a different kind of insurance than a high rate.",
  },
];

// ---------------------------------------------------------------------------
// STAGE 2 — PLANNING: build a zero-based $10,000 bond portfolio across the
// same six categories just scouted. Duration values below are simplified
// stand-ins for the source material's rule of thumb: roughly 1% price move
// per 1% rate move, per point of duration.
// ---------------------------------------------------------------------------
export const BOND_CATEGORIES = [
  { id: "tbill", label: "T-Bills", icon: "🛡️", coupon: 5.1, duration: 0.5, credit: "LOW", term: "SHORT", flavor: "Short-Term Wall — low reward, but barely moves when rates shift." },
  { id: "tbond", label: "T-Bonds", icon: "🏰", coupon: 4.2, duration: 18, credit: "LOW", term: "LONG", flavor: "Long-Term Wall — zero default risk, but the biggest price swings in the fortress." },
  { id: "muni", label: "Munis", icon: "🏛️", coupon: 3.5, duration: 8, credit: "LOW", term: "MEDIUM", flavor: "Tax Shield — modest coupon, but often tax-exempt." },
  { id: "igcorp", label: "IG Corporate", icon: "🏢", coupon: 5.8, duration: 6, credit: "MEDIUM", term: "MEDIUM", flavor: "Steady Corp Wall — a bit more yield, a bit more credit risk." },
  { id: "junk", label: "High-Yield", icon: "⚔️", coupon: 9.5, duration: 4, credit: "HIGH", term: "MEDIUM", flavor: "Junk Bond Gate — the highest coupon in the fortress, and the highest default risk." },
  { id: "tips", label: "TIPS", icon: "🌾", coupon: 1.8, duration: 5, credit: "LOW", term: "MEDIUM", flavor: "Inflation Shield — principal grows with inflation, whatever rates do." },
];

export const STRUCTURE_OPTIONS = [
  {
    id: "individual",
    label: "Individual Bonds",
    description: "Hold each bond to maturity. Full principal back at maturity, barring default — price dips along the way are just paper, if you don't sell.",
  },
  {
    id: "fund",
    label: "Bond Fund",
    description: "A managed basket with no single maturity date. Constantly trades to hold a target duration — more convenient, but exposed differently when rates move.",
  },
];

// ---------------------------------------------------------------------------
// STAGE 3 — CAMPAIGN: a year of the kind of things that actually happen to a
// bond portfolio. Encounter 5 has a structure-aware variant.
// ---------------------------------------------------------------------------
export const RATE_HIKE_ENCOUNTER = {
  id: "c1",
  label: "The Fed Raises Rates",
  text: "The Fed hikes rates by 1% to fight inflation. Every bond issued yesterday is suddenly less attractive than one issued today — the Golden Rule of Bonds, in action.",
  choices: [
    { id: "hold", label: "Hold everything — you bought these to keep, not to trade", tag: "good", result: "Prices dipped on paper. Since nothing was sold, nothing was actually lost.", effect: { panicSold: false } },
    { id: "panic", label: "Sell your longest-duration holdings to \u201clock in what's left\u201d", tag: "bad", result: "That turned a paper dip into a real, permanent loss.", effect: { panicSold: true } },
  ],
};

export const CREDIT_ENCOUNTER = {
  id: "c2",
  label: "Credit Downgrade",
  text: "Rocket Startup Inc. — issuer of your high-yield bond, if you hold one — gets downgraded after missing a growth target.",
  choices: [
    { id: "diversify", label: "Note it, and keep high-yield to a small slice next time", tag: "good", result: "Concentration risk is the real lesson here — one issuer shouldn't be able to sink the fortress.", effect: {} },
    { id: "double-down", label: "Buy more, betting on a rebound", tag: "bad", result: "The rebound didn't come. That bet made a bad situation bigger.", effect: { doubleDownJunk: true } },
  ],
};

export const INFLATION_ENCOUNTER = {
  id: "c3",
  label: "Inflation Spike",
  text: "Inflation jumps to 6% this year — well above most of the fortress's fixed coupons.",
  choices: [
    { id: "tips", label: "Lean on your TIPS allocation — this is exactly what it's for", tag: "good", result: "TIPS principal adjusted upward with inflation, protecting real purchasing power.", effect: { inflationChoice: "tips" } },
    { id: "chase", label: "Chase extra yield elsewhere to try to \u201coutrun\u201d inflation", tag: "bad", result: "The reach for yield added risk without actually solving the inflation problem.", effect: { inflationChoice: "chase", oneTimeCost: 300 } },
  ],
};

export const CALL_ENCOUNTER = {
  id: "c4",
  label: "Early Call",
  text: "One of your investment-grade corporate bonds gets called early — the issuer refinances at a lower rate now that rates have dropped.",
  choices: [
    { id: "reinvest", label: "Reinvest the returned principal into a diversified mix", tag: "good", result: "Call risk realized, but handled calmly — no scramble.", effect: { oneTimeGain: 100 } },
    { id: "chase-yield", label: "Chase the highest yield you can find, rating unchecked", tag: "bad", result: "The new bond paid more because it was riskier — and it showed.", effect: { oneTimeCost: 400 } },
  ],
};

export const STRUCTURE_ENCOUNTER = {
  individual: {
    id: "c5",
    label: "A Second Wobble",
    text: "Rates tick up again. Your individual bonds' market prices dip a little further.",
    choices: [
      { id: "hold", label: "Stay the course — maturity is still maturity", tag: "good", result: "Full principal is still coming back on schedule, regardless of today's price.", effect: {} },
      { id: "sell-early", label: "Sell into the secondary market anyway, just to be safe", tag: "bad", result: "That \u201csafety\u201d move locked in a loss that time alone would have erased.", effect: { earlySell: true } },
    ],
  },
  fund: {
    id: "c5",
    label: "A Second Wobble",
    text: "Rates tick up again. Your bond fund has no maturity date to wait out — it's constantly buying and selling to hold its target duration.",
    choices: [
      { id: "hold", label: "Stay invested — the fund will gradually buy new bonds at the higher yields", tag: "good", result: "Rough short-term, but the fund is already reinvesting at better rates.", effect: {} },
      { id: "panic-sell-fund", label: "Sell the fund in a panic", tag: "bad", result: "Selling turned an unrealized dip into a fully realized loss.", effect: { earlySell: true } },
    ],
  },
};

export const AUTOROLL_ENCOUNTER = {
  id: "c6",
  label: "Auto-Rolling",
  text: "Your brokerage offers to automatically reinvest principal from maturing short-term bonds into new ones the moment they mature.",
  choices: [
    { id: "on", label: "Turn on auto-rolling", tag: "good", result: "The short-term ladder keeps working without you having to babysit it.", effect: { oneTimeGain: 150 } },
    { id: "off", label: "Leave it off — you'll deal with it manually eventually", tag: "bad", result: "That cash sat idle for weeks, earning nothing.", effect: { oneTimeCost: 150 } },
  ],
};

export function buildEncounters(structure) {
  return [RATE_HIKE_ENCOUNTER, CREDIT_ENCOUNTER, INFLATION_ENCOUNTER, CALL_ENCOUNTER, STRUCTURE_ENCOUNTER[structure], AUTOROLL_ENCOUNTER];
}

// ---------------------------------------------------------------------------
// MATH HELPERS
// ---------------------------------------------------------------------------

export function sumEffects(log, field) {
  return log.reduce((total, entry) => total + (entry.choice.effect?.[field] || 0), 0);
}

export function weightedAverage(allocations, field) {
  const total = Object.values(allocations).reduce((s, v) => s + v, 0);
  if (total === 0) return 0;
  return BOND_CATEGORIES.reduce((sum, cat) => sum + (allocations[cat.id] || 0) * cat[field], 0) / total;
}

export function annualCouponIncome(allocations) {
  return BOND_CATEGORIES.reduce((sum, cat) => sum + (allocations[cat.id] || 0) * (cat.coupon / 100), 0);
}
